/**
 * Klarna Payments button — widget flow.
 *
 * Loads the Klarna JS API, creates a session on the backend, initializes
 * the widget with the returned client_token, then calls Klarna.Payments.
 * authorize() to get an authorization_token which the backend converts
 * into a captured Klarna order.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { shopPaymentsAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { toast } from 'sonner';

const KLARNA_SDK_URL = 'https://x.klarnacdn.net/kp/lib/v1/api.js';

function loadKlarnaSDK() {
  return new Promise((resolve, reject) => {
    if (window.Klarna) return resolve();
    const existing = document.querySelector(`script[src="${KLARNA_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = KLARNA_SDK_URL;
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function KlarnaPayButton({ onApproved, disabled }) {
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [sessionState, setSessionState] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        await loadKlarnaSDK();
        const r = await shopPaymentsAPI.klarnaCreateSession({ method: 'klarna' });
        if (cancelled) return;
        setSessionState({ order_id: r.data.order_id, session_id: r.data.session_id });
        window.Klarna.Payments.init({ client_token: r.data.client_token });
        window.Klarna.Payments.load(
          { container: '#kr-klarna-container', payment_method_category: 'pay_over_time' },
          () => { if (!cancelled) setReady(true); },
        );
      } catch (e) {
        if (!cancelled) toast.error(getErrorMessage(e, 'Klarna failed to initialize'));
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  const pay = () => {
    if (!ready || !window.Klarna || !sessionState) return;
    setBusy(true);
    window.Klarna.Payments.authorize(
      { payment_method_category: 'pay_over_time' },
      {},
      async (result) => {
        if (!result?.approved || !result?.authorization_token) {
          setBusy(false);
          toast.error(result?.error?.invalid_fields?.join(', ') || 'Klarna did not approve');
          return;
        }
        try {
          const r = await shopPaymentsAPI.klarnaCreateOrder({
            kr_order_id: sessionState.order_id,
            authorization_token: result.authorization_token,
          });
          onApproved && await onApproved(r.data);
        } catch (e) {
          toast.error(getErrorMessage(e, 'Klarna capture failed'));
        } finally { setBusy(false); }
      },
    );
  };

  return (
    <div className="space-y-2" data-testid="klarna-section">
      <div id="kr-klarna-container" ref={containerRef} />
      <Button onClick={pay} disabled={!ready || busy || disabled}
              className="w-full bg-[#FFA8CD] text-black hover:bg-[#FF87BB]"
              data-testid="klarna-pay-btn">
        {busy && <Loader2 size={13} className="mr-1.5 animate-spin" />}
        Pay with Klarna
      </Button>
    </div>
  );
}
